import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { Table, Input, Button, Badge, Field, SortControl, sortRows } from "@/lib/ui";

export default function Holidays() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name: "", from_date: "", to_date: "", note: "" });
  const [editing, setEditing] = useState(null);
  const [sort, setSort] = useState({ field: "name", dir: "asc" });
  const SORT_FIELDS = [
    { key: "from_date", label: "From Date", type: "date" },
    { key: "to_date", label: "To Date", type: "date" },
    { key: "name", label: "Holiday", type: "str" },
    { key: "status", label: "Status", type: "str" },
  ];

  const load = () => api.get("/admin/holidays").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!form.name || !form.from_date || !form.to_date) { toast.error("Fill name and dates"); return; }
    try { await api.post("/admin/holidays", { ...form, status: "active" }); toast.success("Holiday added"); setForm({ name: "", from_date: "", to_date: "", note: "" }); load(); }
    catch (er) { toast.error(apiErr(er)); }
  };
  const del = async (id) => { await api.delete(`/admin/holidays/${id}`); toast.success("Deleted"); load(); };
  const toggle = async (h) => { await api.put(`/admin/holidays/${h.id}`, { ...h, status: h.status === "active" ? "inactive" : "active" }); load(); };
  const saveEdit = async () => {
    if (!editing.name || !editing.from_date || !editing.to_date) { toast.error("Fill name and dates"); return; }
    try {
      await api.put(`/admin/holidays/${editing.id}`, {
        name: editing.name, from_date: editing.from_date, to_date: editing.to_date, note: editing.note || "", status: editing.status || "active",
      });
      toast.success("Holiday updated"); setEditing(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div>
      <h1 className="font-head text-2xl font-extrabold text-slate-900">Holiday Calendar</h1>
      <p className="mt-1 text-sm text-slate-500">Informational only — does not pause the 48-hour approval timer or modify any subscription.</p>

      <form onSubmit={add} className="mt-4 grid gap-3 rounded-xl border border-slate-200 bg-white p-4 sm:grid-cols-4">
        <Field label="Holiday Name"><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="hol-name" /></Field>
        <Field label="From Date"><Input type="date" value={form.from_date} onChange={(e) => setForm((f) => ({ ...f, from_date: e.target.value }))} data-testid="hol-from" /></Field>
        <Field label="To Date"><Input type="date" value={form.to_date} onChange={(e) => setForm((f) => ({ ...f, to_date: e.target.value }))} data-testid="hol-to" /></Field>
        <Field label="Note"><Input value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} /></Field>
        <div className="sm:col-span-4"><Button type="submit" data-testid="hol-add"><i className="fa-solid fa-plus" /> Add Holiday</Button></div>
      </form>

      <div className="mt-4">
        <div className="mb-3"><SortControl fields={SORT_FIELDS} value={sort} onChange={setSort} testid="hol-sort" /></div>
        <Table head={["Holiday", "From", "To", "Note", "Status", "Actions"]}>
          {sortRows(rows, SORT_FIELDS, sort).map((h) => (
            <tr key={h.id} data-testid={`hol-row-${h.id}`}>
              <td className="px-4 py-3 font-semibold">{h.name}</td><td className="px-4 py-3">{h.from_date}</td><td className="px-4 py-3">{h.to_date}</td>
              <td className="px-4 py-3 text-slate-500">{h.note}</td><td className="px-4 py-3"><Badge tone={h.status === "active" ? "green" : "slate"}>{h.status}</Badge></td>
              <td className="px-4 py-3">
                <div className="flex gap-1">
                  <Button variant="outline" onClick={() => setEditing({ ...h })} data-testid={`hol-edit-${h.id}`}><i className="fa-solid fa-pen" /></Button>
                  <Button variant="ghost" onClick={() => toggle(h)}>{h.status === "active" ? "Deactivate" : "Activate"}</Button>
                  <Button variant="ghost" onClick={() => del(h.id)}><i className="fa-solid fa-trash text-red-500" /></Button>
                </div>
              </td>
            </tr>
          ))}
        </Table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setEditing(null)}>
          <div className="w-full max-w-lg rounded-2xl bg-white p-6" onClick={(e) => e.stopPropagation()} data-testid="hol-edit-modal">
            <h2 className="font-head text-xl font-extrabold text-slate-900">Edit Holiday</h2>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <Field label="Holiday Name"><Input value={editing.name} onChange={(e) => setEditing((s) => ({ ...s, name: e.target.value }))} data-testid="hol-edit-name" /></Field>
              <Field label="Note"><Input value={editing.note || ""} onChange={(e) => setEditing((s) => ({ ...s, note: e.target.value }))} /></Field>
              <Field label="From Date"><Input type="date" value={editing.from_date} onChange={(e) => setEditing((s) => ({ ...s, from_date: e.target.value }))} /></Field>
              <Field label="To Date"><Input type="date" value={editing.to_date} onChange={(e) => setEditing((s) => ({ ...s, to_date: e.target.value }))} /></Field>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              <Button onClick={saveEdit} data-testid="hol-edit-save">Save</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
